import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import { useGetAffiliation } from '~/hooks/apis/agent/useGetAffiliation';
import { useUpdateAffiliation } from '~/hooks/apis/agent/useUpdateAffiliation';
import { useUpdateAffiliationUsersTier } from '~/hooks/apis/agent/useUpdateAffiliationUsersTier';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { AffiliationRules, AffiliationWithRules, Tier } from '~/types/services/agentHub';

import EditTierForm from '../Tier/EditTierForm';

interface Props {
  id: string | null;
  initialAffiliation?: AffiliationWithRules;
  onClose?: () => void;
  opened?: boolean;
}

type AffiliationRuleKey = Extract<keyof AffiliationRules, string>;

const affiliationRuleOptions: Array<{ key: AffiliationRuleKey; label: string }> = [
  { key: 'canBookCarHire', label: 'Can book car hire' },
  { key: 'canBookCruise', label: 'Can book cruise' },
  { key: 'canBookAllFlights', label: 'Can book all flights' },
];

export default function AffiliationEditorModal({ id, initialAffiliation, onClose, opened = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const brand = useCurrentBrand();

  const regions = useMemo(() => ['world', ...getRegions(brand).map((region) => region.code)], [brand]);

  const [open, setOpen] = useState(opened);
  const [affiliationDetails, setAffiliationDetails] = useState<AffiliationWithRules | undefined>(undefined);
  const [tier, setTier] = useState<Tier>('tier1');
  const [showEditTierForm, setShowEditTierForm] = useState(false);

  // Query
  const {
    data: affiliationData,
    isFetching: isLoadingAffiliation,
    isError,
  } = useGetAffiliation(id ?? '', { enabled: open && !!id });

  // Mutations
  const updateAffiliationMutation = useUpdateAffiliation({
    onSuccess: () => {
      enqueueSnackbar('Register updated successfully');
      closeModal();
    },
    onError: (error) => {
      enqueueSnackbar(`There was an error updating the affiliation: ${error.message}`, { variant: 'error' });
    },
  });

  const updateTierMutation = useUpdateAffiliationUsersTier({
    onSuccess: () => {
      enqueueSnackbar('Tier updated successfully');
      setShowEditTierForm(false);
    },
    onError: (error) => {
      enqueueSnackbar(`There was an error updating the tier: ${error.message}`, { variant: 'error' });
    },
  });

  // Sync local state with fetched data
  useEffect(() => {
    if (initialAffiliation) {
      setAffiliationDetails(initialAffiliation);
    }
  }, [initialAffiliation]);

  useEffect(() => {
    if (affiliationData) {
      setAffiliationDetails(affiliationData);
    }
  }, [affiliationData]);

  const onUpdateTier = useCallback(async () => {
    if (affiliationDetails) {
      updateTierMutation.mutate({
        name: affiliationDetails.name,
        region: affiliationDetails.region,
        tier,
      });
    }
  }, [affiliationDetails, tier, updateTierMutation]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setShowEditTierForm(false);
    onClose?.();
  }, [onClose]);

  const save = useCallback(() => {
    if (!affiliationDetails || !id) {
      return;
    }

    if (Object.entries(affiliationDetails).some(([_, value]) => typeof value !== 'boolean' && !value)) {
      enqueueSnackbar('Please fill all the fields', { variant: 'error' });
      return;
    }

    const { name, region, isManageable, rules } = affiliationDetails;
    updateAffiliationMutation.mutate({
      affiliationId: id,
      data: { name, region, isManageable, rules },
    });
  }, [affiliationDetails, id, enqueueSnackbar, updateAffiliationMutation]);

  useEffect(() => {
    if (opened) {
      setOpen(true);
    }
  }, [opened]);

  const isLoading = isLoadingAffiliation;
  const isSaving = updateAffiliationMutation.isPending;

  return (
    <Dialog onClose={closeModal} open={open}>
      <DialogTitle>Edit Affiliation</DialogTitle>

      <DialogContent>
        {isLoading && <Spinner />}
        {!isLoading && (
          <Stack direction="column" spacing={2}>
            <form onSubmit={(e) => e.preventDefault()} style={{ minWidth: '500px' }}>
              <Stack mt={2} direction="column" spacing={2}>
                <FormGroup>
                  <TextField
                    label="Name"
                    value={affiliationDetails?.name ?? ''}
                    required
                    fullWidth
                    onChange={(e) => setAffiliationDetails({ ...affiliationDetails, name: e.target.value })}
                  />
                </FormGroup>

                <FormControl>
                  <InputLabel id="affiliation-region">Region</InputLabel>
                  <Select
                    labelId="affiliation-region"
                    id="affiliation-region"
                    label="Region"
                    required
                    fullWidth
                    value={affiliationDetails?.region ?? ''}
                    onChange={(e) => setAffiliationDetails({ ...affiliationDetails, region: e.target.value })}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        required={false}
                        checked={affiliationDetails?.isManageable ?? false}
                        onChange={(e) =>
                          setAffiliationDetails({ ...affiliationDetails, isManageable: e.target.checked })
                        }
                      />
                    }
                    label="Managed affiliation"
                  />
                </FormGroup>
                <FormGroup>
                  {affiliationRuleOptions.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          required={false}
                          checked={affiliationDetails?.rules?.[key] ?? false}
                          onChange={(e) =>
                            setAffiliationDetails({
                              ...affiliationDetails,
                              rules: {
                                ...affiliationDetails?.rules,
                                [key]: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>

                {isError && <ErrorDisplay message="There was an error loading the affiliation" severity="error" />}
              </Stack>
            </form>

            <Stack>
              {!showEditTierForm && (
                <Button variant="outlined" color="primary" onClick={() => setShowEditTierForm(true)}>
                  Change agents tier
                </Button>
              )}

              {showEditTierForm && (
                <EditTierForm
                  onSubmit={onUpdateTier}
                  setShowEditTierForm={setShowEditTierForm}
                  setTier={setTier}
                  tier={tier}
                />
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant="text" onClick={closeModal}>
          Close
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
